import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, activateData } from '../../../apiService';
import apiConfig from '../../../apiConfig';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import usePermissions from '../../../hooks/usePermissions';
import {
  Add, Edit, Visibility, Delete, Payment, FilterList, Refresh, Warning, CheckCircle, Cancel, Close, Print, Search as SearchIcon
} from '@mui/icons-material';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Typography, Box, Divider, Grid,
  FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment, Fade,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress
} from '@mui/material';
import PaymentReceiptsModal from './PaymentReceiptsModal';
import Pagination from '../../common/Pagination';
import './PaymentReceiptsIndex.css';
import { printPaymentReceiptPDF } from '../../../utils/paymentReceiptPdfGenerator';


const PaymentReceiptsIndex = () => {
  // Session validation
  useSessionValidation();

  const history = useHistory();
  const { companyId, branchId, token, userId } = useAuth();
  const { canCreate, canEdit, canDelete } = usePermissions('Accounts');

  // Remove old stale permission checks — replaced by usePermissions above

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Data states
  const [paymentReceipts, setPaymentReceipts] = useState([]);
  const [dropdownData, setDropdownData] = useState({ Master: [], AccountHead: [], Bank: [] });
  const [banks, setBanks] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [gstRates, setGstRates] = useState([]);
  const [companyData, setCompanyData] = useState(null);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all'); // payment or receipt
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mounted, setMounted] = useState(false);

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states - initialData is passed to modal; modal owns its own state
  const [initialData, setInitialData] = useState(null);
  const [paymentReceiptDetails, setPaymentReceiptDetails] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete/Activate confirmation modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemNo, setActivateItemNo] = useState('');

  // Cheque status update modal
  const [chequeStatusModalOpen, setChequeStatusModalOpen] = useState(false);
  const [selectedChequeItem, setSelectedChequeItem] = useState(null);
  const [newChequeStatus, setNewChequeStatus] = useState(1);
  const [bulkChequeStatusModalOpen, setBulkChequeStatusModalOpen] = useState(false);
  const [selectedChequeItems, setSelectedChequeItems] = useState([]);
  const [individualChequeStatuses, setIndividualChequeStatuses] = useState({}); // Store individual status for each item

  // Drill-down states for animated expansion - REMOVED (not needed)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (companyId && token) {
      fetchDropdownData();
      fetchPaymentMethods();
      fetchCompanyData();
    }
  }, [companyId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchPaymentReceipts();
    }
  }, [companyId, token, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch functions
  const fetchPaymentReceipts = async () => {
    try {
      const activeResponse = await fetchData('PaymentAndReceiptApi', `/ActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeResponse.response_data || activeResponse || []).map(item => ({ ...item, IsActive: item.IsActive ?? true }));

      let inactiveData = [];
      try {
        const inactiveResponse = await fetchData('PaymentAndReceiptApi', `/InActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveResponse.response_data || inactiveResponse || []).map(item => ({ ...item, IsActive: false }));
      } catch (inactiveError) {
        console.error('Error fetching inactive payment receipts:', inactiveError);
      }

      setPaymentReceipts([...activeData, ...inactiveData]);
    } catch (error) {
      console.error('Error fetching payment receipts:', error);
      setPaymentReceipts([]);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [mastersRes, accountHeadsRes, banksRes] = await Promise.allSettled([
        fetchData('MasterApi', `/${companyId}`, token),
        fetchData('AccHeadApi', `/${companyId}`, token),
        fetchData('BankApi', `/${companyId}`, token),
      ]);
      const mastersList = mastersRes.status === 'fulfilled' ? (mastersRes.value.response_data || mastersRes.value || []) : [];
      const accountHeadsList = accountHeadsRes.status === 'fulfilled' ? (accountHeadsRes.value.response_data || accountHeadsRes.value || []) : [];
      const banksList = banksRes.status === 'fulfilled' ? (banksRes.value.response_data || banksRes.value || []) : [];
      setDropdownData({ Master: mastersList, AccountHead: accountHeadsList, Bank: banksList });
      setBanks(banksList);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await fetchData('PaymentAndReceiptApi', `/PaymentModes/${companyId}`, token);
      const data = response.response_data || response || [];
      setPaymentMethods(data.length > 0 ? data : [
        { Id: 1, Name: 'Cash', IsActive: true },
        { Id: 2, Name: 'Cheque', IsActive: true },
        { Id: 3, Name: 'Online Transfer', IsActive: true },
      ]);
    } catch {
      setPaymentMethods([
        { Id: 1, Name: 'Cash', IsActive: true },
        { Id: 2, Name: 'Cheque', IsActive: true },
        { Id: 3, Name: 'Online Transfer', IsActive: true },
      ]);
    }
  };

  const fetchGstRates = async () => {
    try {
      const response = await fetchData('PaymentAndReceiptApi', `/GetGSTList/${companyId}`, token);
      setGstRates(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching GST rates:', error);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const response = await fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token);
      setCompanyData(response.response_data || response || null);
    } catch (error) {
      console.error('Error fetching company data:', error);
      setCompanyData(null);
    }
  };

  const fetchMaxBillNo = useCallback(async (isType = true) => {
    try {
      const isTypeString = isType.toString();
      const response = await fetchData('PaymentAndReceiptApi', `/MaxBillno/${branchId || 1}/${companyId}/${isTypeString}`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';
      return { billNumber, billPrefix, finYear };
    } catch (error) {
      console.error('Error fetching max bill number:', error);
      return { billNumber: 1, billPrefix: '', finYear: '' };
    }
  }, [branchId, companyId, token]);

  // Fetch specific payment receipt details by ID
  const fetchPaymentReceiptDetails = async (paymentReceiptId) => {
    try {
      const response = await fetchData('PaymentAndReceiptApi', `/PaymentData/${paymentReceiptId}`, token);
      return response.response_data || response || null;
    } catch (error) {
      console.error('Error fetching payment receipt details:', error);
      // Try alternative endpoint if GetById doesn't work
      try {
        const altResponse = await fetchData('PaymentAndReceiptApi', `/${paymentReceiptId}`, token);
        return altResponse.response_data || altResponse || null;
      } catch (altError) {
        console.error('Error with alternative endpoint:', altError);
        return null;
      }
    }
  };

  // Print Payment/Receipt handler — uses Bill-style PDF generator (portrait)
  const handlePrintPaymentReceipt = useCallback(async (paymentReceiptId) => {
    setLoading(true);
    setLoadingMessage('Generating PDF...');

    try {
      const freshData = await fetchPaymentReceiptDetails(paymentReceiptId);

      if (!freshData) {
        setError('Failed to fetch payment receipt details for printing.');
        setLoading(false);
        return;
      }

      const rawData = freshData.PaymentAndReceipt || freshData;
      const purchaseDetails = freshData.PaymentAndReceiptPurchase || [];
      const saleDetails = freshData.PaymentAndReceiptSale || [];

      // Normalise field names — API may return IsTypeReceipt or IsType, PaymentMode or PaymentType, etc.
      const paymentData = {
        ...rawData,
        // Document type: false/0 = Payment, true/1 = Receipt
        IsType: rawData.IsType !== undefined
          ? rawData.IsType
          : (rawData.IsTypeReceipt !== undefined ? !rawData.IsTypeReceipt : false),
        // Payment mode id
        PaymentType: rawData.PaymentType ?? rawData.PaymentMode ?? 1,
        // Cheque / reference number
        ChequeNumber: rawData.ChequeNumber || rawData.ChequeNo || rawData.CheqNo || '',
        // Bill number
        BillNumber: rawData.BillNumber || rawData.BillNo || rawData.BillNo || '',
        // Bank id used for cheque/online (CheqBankId or BankId)
        BankId: rawData.CheqBankId || rawData.BankId || null,
      };

      // Resolve party name — works for Master, AccountHead, or Bank as account type
      const acType = rawData.AcType || 1; // 1=Master, 2=AccountHead, 3=Bank
      let partyName = rawData.MasterName || rawData.AccountHeadName || rawData.BankName || '-';
      let partyAddress = '';
      let partyGST = '';
      let partyContact = '';

      if (acType === 1) {
        // Master
        const master = dropdownData.Master.find(m => m.Id === rawData.MasterId) || {};
        partyName = partyName !== '-' ? partyName : (master.Name || '-');
        partyAddress = master.Address || '';
        partyGST = master.GSTNumber || '';
        partyContact = master.ContactNumber || master.Mobile || '';
      } else if (acType === 2) {
        // Account Head
        const accHead = (dropdownData.AccountHead || []).find(a => a.Id === rawData.AccountHeadId) || {};
        partyName = partyName !== '-' ? partyName : (accHead.Name || '-');
      } else if (acType === 3) {
        // Bank as party
        const bankParty = banks.find(b => b.Id === rawData.BankId) || {};
        partyName = partyName !== '-' ? partyName : (bankParty.Name || '-');
      }

      const masterData = {
        Name: partyName,
        Address: partyAddress,
        GSTNumber: partyGST,
        ContactNumber: partyContact,
      };

      // Resolve transaction bank (cheque/online bank)
      const txBank = banks.find(b => b.Id === paymentData.BankId);
      const bankName = txBank?.Name || '';

      // Resolve payment mode label
      const paymentMethod = paymentMethods.find(pm => pm.Id === paymentData.PaymentType);
      const paymentMethodName = paymentMethod?.Name || 'Cash';

      const isPayment = paymentData.IsType; // false = Payment, true = Receipt (PDF generator uses this)

      // Build table rows — Particulars column shows the account name
      const tableData = [];
      const particulars = acType === 2
        ? (partyName !== '-' ? partyName : 'Account Head Entry')
        : acType === 3
          ? (partyName !== '-' ? partyName : 'Bank Entry')
          : (partyName !== '-' ? partyName : 'General Entry');

      if (!isPayment && purchaseDetails.length > 0) {
        // IsType false = Payment → purchase details
        purchaseDetails.forEach((d, idx) => {
          tableData.push([
            (idx + 1).toString(),
            'Purchase Bill Payment',
            `Bill ID: ${d.PurchaseId || '-'}`,
            parseFloat(d.Amount || 0).toFixed(2),
          ]);
        });
      } else if (isPayment && saleDetails.length > 0) {
        // IsType true = Receipt → sale details
        saleDetails.forEach((d, idx) => {
          tableData.push([
            (idx + 1).toString(),
            'Sale Bill Receipt',
            `Bill ID: ${d.SaleId || '-'}`,
            parseFloat(d.Amount || 0).toFixed(2),
          ]);
        });
      } else {
        // Direct payment/receipt with remarks
        tableData.push([
          '1',
          particulars,
          paymentData.Remarks || `Direct ${!isPayment ? 'Payment' : 'Receipt'}`,
          parseFloat(paymentData.Amount || 0).toFixed(2),
        ]);
      }

      // Generate and print using the new Bill-style PDF generator
      printPaymentReceiptPDF(paymentData, companyData || {}, masterData, bankName, paymentMethodName, tableData, companyId);

      const documentType = !isPayment ? 'PAYMENT VOUCHER' : 'RECEIPT VOUCHER';
      setSuccess(`Document prepared for printing: ${documentType} ${paymentData.BillNumber}`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF.');
    }

    setLoading(false);
  }, [companyId, branchId, token, companyData, dropdownData, banks, paymentMethods]);

  // Handle modal actions
  const handleOpenModal = useCallback(async (action, item = null) => {
    setModalAction(action);
    setSelectedItem(item);

    if (action === 'create') {
      setInitialData(null);
      setPaymentReceiptDetails([]);
    } else if (action === 'edit' || action === 'view') {
      setModalLoading(true);
      try {
        const response = await fetchData('PaymentAndReceiptApi', `/PaymentData/${item.Id}`, token);
        const paymentReceiptData = response.response_data || response;

        // Handle both wrapped { PaymentAndReceipt: {...} } and flat response shapes
        const paymentData = paymentReceiptData.PaymentAndReceipt || paymentReceiptData;

        if (!paymentData || !paymentData.Id) {
          throw new Error('Invalid response structure');
        }

        setInitialData({
          ...paymentData,
          BillDate: paymentData.BillDate ? paymentData.BillDate.split('T')[0] : new Date().toISOString().split('T')[0],
          CheqDate: paymentData.CheqDate ? paymentData.CheqDate.split('T')[0] : '',
          AcType: paymentData.AcType || 1,
        });

        // Set payment receipt details from the API response
        const purchaseDetails = paymentReceiptData.PaymentAndReceiptPurchase || [];
        const saleDetails = paymentReceiptData.PaymentAndReceiptSale || [];

        if (paymentData.IsTypeReceipt === false && purchaseDetails.length > 0) {
          // Payment - show purchase details
          const mappedDetails = purchaseDetails.map(p => ({
            Id: p.PurchaseId,
            Description: p.PurchaseDescription || `Purchase - ${p.PurchaseId}`,
            Amount: p.PurchaseAmount || 0,
            PaidAmount: p.Amount || 0,
            BalanceAmount: 0,
            PendingAmount: 0,
            BillDate: p.BillDate || paymentData.BillDate,
            Type: 'Purchase',
            PaymentAmount: p.Amount || 0,
            IsSelected: true
          }));
          setPaymentReceiptDetails(mappedDetails);
        } else if (paymentData.IsTypeReceipt === true && saleDetails.length > 0) {
          // Receipt - show sale details
          const mappedDetails = saleDetails.map(s => ({
            Id: s.SaleId,
            Description: s.SaleDescription || `Sale - ${s.SaleId}`,
            Amount: s.SaleAmount || 0,
            PaidAmount: s.Amount || 0,
            BalanceAmount: 0,
            PendingAmount: 0,
            BillDate: s.BillDate || paymentData.BillDate,
            Type: 'Sale',
            PaymentAmount: s.Amount || 0,
            IsSelected: true
          }));
          setPaymentReceiptDetails(mappedDetails);
        } else {
          // No specific purchase/sale details - create a general entry
          setPaymentReceiptDetails([{
            Id: paymentData.Id,
            Description: `${paymentData.IsTypeReceipt ? 'Receipt' : 'Payment'} - ${paymentData.BillNo}`,
            Amount: paymentData.Amount,
            PaidAmount: paymentData.Amount,
            BalanceAmount: 0,
            PendingAmount: 0,
            BillDate: paymentData.BillDate,
            Type: paymentData.IsTypeReceipt ? 'Receipt' : 'Payment',
            PaymentAmount: paymentData.Amount,
            IsSelected: true,
            isDirectPayment: true
          }]);
        }

      } catch (error) {
        console.error('Error fetching payment receipt details:', error);
        // Do NOT show error toast — fall back silently using list item data
        setInitialData({
          ...item,
          BillDate: item.BillDate ? item.BillDate.split('T')[0] : new Date().toISOString().split('T')[0],
          CheqDate: item.CheqDate ? item.CheqDate.split('T')[0] : '',
          AcType: item.AcType || 1,
        });

        // Create a basic entry for fallback
        setPaymentReceiptDetails([{
          Id: item.Id,
          Description: `${item.IsTypeReceipt ? 'Receipt' : 'Payment'} - ${item.BillNo}`,
          Amount: item.Amount,
          PaidAmount: item.Amount,
          BalanceAmount: 0,
          PendingAmount: 0,
          BillDate: item.BillDate,
          Type: item.IsTypeReceipt ? 'Receipt' : 'Payment',
          PaymentAmount: item.Amount,
          IsSelected: true,
          isDirectPayment: true
        }]);
      }
      setModalLoading(false);
    }

    setOpenModal(true);
  }, [companyId, branchId, token, userId, fetchMaxBillNo]);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setSelectedItem(null);
    setInitialData(null);
    setPaymentReceiptDetails([]);
  }, []);

  // Handle save — receives formData from modal
  const handleSave = useCallback(async (formData) => {
    setModalLoading(true);
    try {
      const dataToSend = {
          Id: modalAction === 'edit' ? (formData.Id || 0) : 0,
          Companyid: companyId,
          FinYear: formData.FinYear || '',
          BillNo: parseInt(formData.BillNo) || 0,
          BillDate: formData.BillDate,
          AcType: formData.AcType || 1,
          MasterId: formData.MasterId || null,
          AccountHeadId: formData.AccountHeadId || null,
          BankId: formData.BankId || null,
          Amount: parseFloat(formData.Amount) || 0,
          IsCancelled: formData.IsCancelled || false,
          IsTypeReceipt: formData.IsTypeReceipt || false,
          PaymentMode: formData.PaymentMode || 1,
          ChequeNo: formData.ChequeNo || '',
          CheqDate: formData.CheqDate || '0001-01-01',
          CheqBankId: formData.CheqBankId || null,
          Remarks: formData.Remarks || null,
          CreatedBy: modalAction === 'create' ? userId : (formData.CreatedBy || userId),
          CreatedDate: modalAction === 'create' ? new Date().toISOString().split('T')[0] : (formData.CreatedDate || new Date().toISOString().split('T')[0]),
          ModifiedBy: modalAction === 'edit' ? userId : null,
          ModifiedDate: modalAction === 'edit' ? new Date().toISOString().split('T')[0] : null,
      };


      // No purchase/sale detail lines needed with new model

      if (modalAction === 'create') {
        await postData('PaymentAndReceiptApi','', dataToSend, token);
        setSuccess(`${formData.IsTypeReceipt ? 'Receipt' : 'Payment'} created successfully!`);
      } else {
        await updateData('PaymentAndReceiptApi', formData.Id, dataToSend, token);
        setSuccess(`${formData.IsTypeReceipt ? 'Receipt' : 'Payment'} updated successfully!`);
      }

      handleCloseModal();
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error saving payment receipt:', error);
      setError(`Failed to save ${formData.IsTypeReceipt ? 'receipt' : 'payment'}.`);
    }
    setModalLoading(false);
  }, [companyId, branchId, userId, modalAction, handleCloseModal, token]);

  // Handle delete
  const handleDelete = async () => {
    try {
      await deleteData('PaymentAndReceiptApi', deleteItemId, token);
      setSuccess('Payment/Receipt deleted successfully!');
      setDeleteModalOpen(false);
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error deleting payment receipt:', error);
      setError('Failed to delete payment receipt.');
    }
  };

  // Handle activate
  const handleActivate = async () => {
    try {
      await activateData('PaymentAndReceiptApi', activateItemId, token);
      setSuccess('Payment/Receipt activated successfully!');
      setActivateModalOpen(false);
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error activating payment receipt:', error);
      setError('Failed to activate payment receipt.');
    }
  };

  // Wrapper functions for new UI
  const handleDeleteConfirm = async () => {
    await handleDelete();
  };

  const handleActivateConfirm = async () => {
    await handleActivate();
  };

  const handleSavePaymentReceipt = async (formData) => {
    await handleSave(formData);
  };
 

  // Handle bulk cheque status update with individual statuses
  const handleBulkUpdateChequeStatus = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Updating cheque statuses...');

      const updatePromises = selectedChequeItems.map(async (item) => {
        const individualStatus = individualChequeStatuses[item.Id] || newChequeStatus;

        const updatePayload = {
          PaymentAndReceipt: {
            Id: item.Id,
            Companyid: companyId,
            BranchId: branchId,
            MasterId: item.MasterId,
            SubHeadId: item.SubHeadId || 0,
            PartyId: item.PartyId || item.MasterId,
            BillDate: item.BillDate,
            BillNumber: item.BillNumber,
            FinYear: item.FinYear || new Date().getFullYear().toString(),
            IsTypeReceipt: item.IsTypeReceipt,
            IsGST: item.IsGST || false,
            GSTHeadId: item.GSTHeadId || 0,
            IsSaleAndPurchase: item.IsSaleAndPurchase || true,
            PaymentType: item.PaymentType,
            ChequeDate: item.ChequeDate,
            ChequeNumber: item.ChequeNumber,
            BankId: item.BankId,
            chequeDetails: item.chequeDetails,
            ChequeStatusId: individualStatus, // Use individual status for each item
            Amount: item.Amount,
            GSTRate: item.GSTRate || 0,
            CGSTAmount: item.CGSTAmount || 0,
            SGSTAmount: item.SGSTAmount || 0,
            IGSTAmount: item.IGSTAmount || 0,
            RoundOff: item.RoundOff || 0,
            TotalAmount: item.TotalAmount,
            Remarks: item.Remarks,
            IsActive: item.IsActive,
            CreatedBy: item.CreatedBy,
            UpdatedBy: userId,
            CreatedDate: item.CreatedDate,
            UpdatedDate: new Date().toISOString()
          }
        };

        return updateData('PaymentAndReceiptApi', item.Id, updatePayload, token);
      });

      await Promise.all(updatePromises);

      setSuccess(`${selectedChequeItems.length} cheque statuses updated successfully!`);
      setBulkChequeStatusModalOpen(false);
      setSelectedChequeItems([]);
      setIndividualChequeStatuses({});
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error updating cheque statuses:', error);
      setError('Failed to update cheque statuses.');
    } finally {
      setLoading(false);
    }
  };

  // Open cheque status update modal
  const handleOpenChequeStatusModal = (item) => {
    setSelectedChequeItem(item);
    setNewChequeStatus(item.ChequeStatusId || 1);
    setChequeStatusModalOpen(true);
  };

  // Get cheque payment method ID dynamically
  const getChequePaymentMethodId = () => {
    console.log('=== GETTING CHEQUE PAYMENT METHOD ID ===');
    console.log('Available payment methods:', paymentMethods);

    const chequeMethod = paymentMethods.find(pm =>
      pm.Name && pm.Name.toLowerCase().includes('cheque')
    );

    console.log('Found cheque method:', chequeMethod);

    const chequeId = chequeMethod ? chequeMethod.Id : 2;
    console.log('Using cheque payment method ID:', chequeId);
    console.log('=== END CHEQUE METHOD DEBUG ===');

    return chequeId;
  };

  // Handle individual cheque status update (single item)
  const handleIndividualChequeStatusUpdate = async (item, newStatus) => {
    try {
      setLoading(true);
      setLoadingMessage(`Updating cheque status for ${item.BillNumber}...`);

      const updatePayload = {
        PaymentAndReceipt: {
          Id: item.Id,
          Companyid: companyId,
          BranchId: branchId,
          MasterId: item.MasterId,
          SubHeadId: item.SubHeadId || 0,
          PartyId: item.PartyId || item.MasterId,
          BillDate: item.BillDate,
          BillNumber: item.BillNumber,
          FinYear: item.FinYear || new Date().getFullYear().toString(),
          IsType: item.IsType,
          IsGST: item.IsGST || false,
          GSTHeadId: item.GSTHeadId || 0,
          IsSaleAndPurchase: item.IsSaleAndPurchase || true,
          PaymentType: item.PaymentType,
          ChequeDate: item.ChequeDate,
          ChequeNumber: item.ChequeNumber,
          BankId: item.BankId,
          chequeDetails: item.chequeDetails,
          ChequeStatusId: newStatus,
          Amount: item.Amount,
          GSTRate: item.GSTRate || 0,
          CGSTAmount: item.CGSTAmount || 0,
          SGSTAmount: item.SGSTAmount || 0,
          IGSTAmount: item.IGSTAmount || 0,
          RoundOff: item.RoundOff || 0,
          TotalAmount: item.TotalAmount,
          Remarks: item.Remarks,
          IsActive: item.IsActive,
          CreatedBy: item.CreatedBy,
          UpdatedBy: userId,
          CreatedDate: item.CreatedDate,
          UpdatedDate: new Date().toISOString()
        }
      };

      await updateData('PaymentAndReceiptApi', item.Id, updatePayload, token);

      const statusText = newStatus === 1 ? 'Pending' :
        newStatus === 2 ? 'Passed' :
          newStatus === 3 ? 'Bounced' : 'Cancelled';

      setSuccess(`Cheque status updated to "${statusText}" for ${item.BillNumber}!`);

      // Update the individual status in state
      setIndividualChequeStatuses(prev => ({
        ...prev,
        [item.Id]: newStatus
      }));

      // Refresh the main data
      await fetchPaymentReceipts();

    } catch (error) {
      console.error('Error updating individual cheque status:', error);
      setError(`Failed to update cheque status for ${item.BillNumber}.`);
    } finally {
      setLoading(false);
    }
  };

  // Handle individual cheque status change
  const handleIndividualStatusChange = (itemId, newStatus) => {
    setIndividualChequeStatuses(prev => ({
      ...prev,
      [itemId]: newStatus
    }));
  };

  // Get status color and text
  const getStatusInfo = (statusId) => {
    switch (statusId) {
      case 1:
        return { text: 'Pending', color: '#f59e0b', bgColor: '#fef3c7', icon: '⏳' };
      case 2:
        return { text: 'Passed', color: '#10b981', bgColor: '#d1fae5', icon: '✅' };
      case 3:
        return { text: 'Bounced', color: '#ef4444', bgColor: '#fee2e2', icon: '❌' };
      case 4:
        return { text: 'Cancelled', color: '#6b7280', bgColor: '#f3f4f6', icon: '🚫' };
      default:
        return { text: 'Unknown', color: '#6b7280', bgColor: '#f3f4f6', icon: '❓' };
    }
  };

  // Open bulk cheque status update modal
  const handleOpenBulkChequeStatusModal = () => {
    console.log('=== OPENING BULK CHEQUE STATUS MODAL ===');

    const chequePaymentMethodId = getChequePaymentMethodId();
    console.log('Filtering items with PaymentType:', chequePaymentMethodId);
    console.log('Total filtered data items:', filteredData.length);

    const chequeItems = filteredData.filter(item => {
      const isPaymentTypeMatch = item.PaymentType === chequePaymentMethodId;
      const isActive = item.IsActive;
      const isStatusUpdateable = item.ChequeStatusId === null || item.ChequeStatusId === 0 || item.ChequeStatusId === 1;
      const isMatch = isPaymentTypeMatch && isActive && isStatusUpdateable;

      console.log(`Item ${item.Id} (${item.BillNumber}): PaymentType=${item.PaymentType}, IsActive=${item.IsActive}, ChequeStatusId=${item.ChequeStatusId}, Updateable=${isStatusUpdateable}, Match=${isMatch}`);
      return isMatch;
    });

    console.log('Found cheque items:', chequeItems.length);
    console.log('Cheque items details:', chequeItems);

    if (chequeItems.length === 0) {
      console.log('No updateable cheque items found - showing error');
      setError('No pending cheque payments found to update. Only cheques with Pending status can be updated.');
      return;
    }

    setSelectedChequeItems(chequeItems);
    setNewChequeStatus(2); // Default to "Passed"

    // Initialize individual statuses with default value
    const initialStatuses = {};
    chequeItems.forEach(item => {
      initialStatuses[item.Id] = item.ChequeStatusId || 1; // Use current status or default to "Pending"
    });
    setIndividualChequeStatuses(initialStatuses);

    console.log('Initial statuses:', initialStatuses);
    console.log('=== END MODAL OPENING DEBUG ===');

    setBulkChequeStatusModalOpen(true);
  };

  // Filter and search logic
  const filteredData = useMemo(() => paymentReceipts.filter(item => {
    const matchesSearch = !searchTerm ||
      item.BillNumber?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.MasterName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.Remarks?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && item.IsActive) ||
      (filterStatus === 'inactive' && !item.IsActive);

    const matchesType = filterType === 'all' ||
      (filterType === 'payment' && item.IsType) ||
      (filterType === 'receipt' && !item.IsType);

    return matchesSearch && matchesStatus && matchesType;
  }), [paymentReceipts, searchTerm, filterStatus, filterType]);

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = useMemo(
    () => filteredData.slice(startIndex, startIndex + rowsPerPage),
    [filteredData, startIndex, rowsPerPage]
  );

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getPaymentTypeText = (paymentType) => {
    const paymentMethod = paymentMethods.find(pm => pm.Id === paymentType);
    return paymentMethod ? paymentMethod.Name : 'Cash';
  };

  // Handle expand row functionality - REMOVED (not needed)

  if (!mounted) return null;

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ minWidth: '100%', background: '#fdf6ec', padding: '10px', position: 'relative' }}>
        {loading && <LogoLoader open={loading} message="Loading Payment & Receipts..." />}

        <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', color: 'black', fontFamily: 'Times New Romans' }}>
          {/* Header Section */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
            <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', fontFamily: 'Times New Romans', textTransform: 'uppercase' }}>
              <span style={{ color: 'black' }}>💳 Payment & Receipts</span>
            </h4>
          </div>

          {/* Date Filters, Search and Create Button Row */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
              <TextField
                type="date" size="small" value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                sx={{ width: '140px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '11px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } } }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
              <TextField
                type="date" size="small" value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                sx={{ width: '140px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '11px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } } }}
              />
            </div>
            <TextField
              fullWidth size="small" placeholder="Search by voucher, party or remarks..." value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: '#c9a84c', fontSize: '20px' }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: '200px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '13px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } } }}
            />
            <Tooltip title="Create a new payment/receipt" arrow>
              {canCreate && (
              <Button variant="contained" onClick={() => handleOpenModal('create')}
                style={{ background: 'linear-gradient(135deg, #5c3d1e 0%, #5c3d1e 100%)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', border: 'none', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(92,61,30,0.4)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontSize: '14px' }}>+</span> Create
              </Button>
              )}
            </Tooltip>
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
              <Table sx={{ minWidth: 650 }} aria-label="payment receipts table">
                <TableHead>
                  <TableRow style={{ background: 'linear-gradient(135deg, #5c3d1e 0%, #5c3d1e 100%)', height: '27px' }}>
                    {['Voucher No', 'Date', 'Type', 'Party', 'Payment Type', 'Amount', 'Status', 'Actions'].map((h, i) => (
                      <TableCell key={i} style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px', textAlign: i === 7 ? 'right' : 'left' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} style={{ textAlign: 'center', padding: '20px', color: '#999', fontSize: '13px' }}>
                        No payment receipts found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((item, index) => (
                      <TableRow key={item.Id} style={{ background: index % 2 === 0 ? '#ffffff' : '#fdf6ec', borderBottom: '2px solid #f0f0f0' }}>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: '600' }}>
                          {item.BillNo}
                          <div style={{ fontSize: '10px', color: '#999', fontWeight: '400' }}>FY: {item.FinYear}</div>
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                          {formatDate(item.BillDate)}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                          <Chip
                            label={item.IsTypeReceipt ? 'Payment' : 'Receipt'}
                            size="small"
                            style={{ fontSize: '10px', height: '18px', background: item.IsType ? '#fee2e2' : '#d1fae5', color: item.IsType ? '#dc2626' : '#065f46', fontWeight: '600' }}
                          />
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: '500' }}>
                          {item.MasterName || 'N/A'}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                          {getPaymentTypeText(item.PaymentMode)}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2e7d32', fontWeight: '600' }}>
                          {formatCurrency(item.Amount)}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                          <span style={{ fontSize: '10px', fontWeight: '600', padding: '1px 6px', borderRadius: '4px', background: item.IsActive ? '#e8f5e9' : '#fce4ec', color: item.IsActive ? '#2e7d32' : '#c62828' }}>
                            {item.IsActive ? 'Active' : 'Inactive'}
                          </span>
                        </TableCell>
                        <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Tooltip title="View Details" arrow>
                              <Button size="small" onClick={() => handleOpenModal('view', item)}
                                style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', boxShadow: '0 1px 4px rgba(102,126,234,0.3)' }}>
                                👁️
                              </Button>
                            </Tooltip>
                            <Tooltip title="Print / Download PDF" arrow>
                              <Button size="small" onClick={() => handlePrintPaymentReceipt(item.Id)}
                                style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', boxShadow: '0 1px 4px rgba(17,153,142,0.3)' }}>
                                🖨️
                              </Button>
                            </Tooltip>
                            {canEdit && (
                            <Tooltip title="Edit" arrow>
                              <Button size="small" onClick={() => handleOpenModal('edit', item)}
                                style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', boxShadow: '0 1px 4px rgba(245,158,11,0.3)' }}>
                                ✏️
                              </Button>
                            </Tooltip>
                            )}
                            {canDelete && (
                              <Tooltip title={item.IsActive ? 'Delete' : 'Activate'} arrow>
                                <Button size="small"
                                  onClick={() => {
                                    if (item.IsActive) { setDeleteItemId(item.Id); setDeleteItemNo(item.BillNumber); setDeleteModalOpen(true); }
                                    else { setActivateItemId(item.Id); setActivateItemNo(item.BillNumber); setActivateModalOpen(true); }
                                  }}
                                  style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', boxShadow: item.IsActive ? '0 1px 4px rgba(255,107,107,0.3)' : '0 1px 4px rgba(78,205,196,0.3)' }}>
                                  {item.IsActive ? '🗑️' : '✅'}
                                </Button>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredData.length}
            itemsPerPage={rowsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
            onItemsPerPageChange={(newSize) => {
              setRowsPerPage(newSize);
              setCurrentPage(1);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}>
          <DialogTitle style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>Delete Record?</DialogTitle>
          <DialogContent>
            <DialogContentText style={{ fontSize: '13px', color: '#666' }}>Are you sure you want to delete voucher {deleteItemNo}? This action cannot be undone.</DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button onClick={() => setDeleteModalOpen(false)} style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)', color: '#ffffff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: '600', textTransform: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', minHeight: '26px' }}>
              <span>❌</span> Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#ffffff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: '600', textTransform: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', minHeight: '26px' }}>
              <span>✅</span> Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Activate Confirmation Dialog */}
        <Dialog open={activateModalOpen} onClose={() => setActivateModalOpen(false)} PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}>
          <DialogTitle style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>Activate Record?</DialogTitle>
          <DialogContent>
            <DialogContentText style={{ fontSize: '13px', color: '#666' }}>Are you sure you want to activate voucher {activateItemNo}?</DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button onClick={() => setActivateModalOpen(false)} style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)', color: '#ffffff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: '600', textTransform: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', minHeight: '26px' }}>
              <span>❌</span> Cancel
            </Button>
            <Button onClick={handleActivateConfirm} style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#ffffff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: '600', textTransform: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', minHeight: '26px' }}>
              <span>✅</span> Confirm Activate
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Cheque Status Update Modal */}
        <Dialog
          open={bulkChequeStatusModalOpen}
          onClose={() => {
            setBulkChequeStatusModalOpen(false);
            setIndividualChequeStatuses({});
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Payment />
              Update Cheque Status
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body2" sx={{ mb: 2, color: '#6b7280' }}>
              Update the status of pending cheque payments below:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {selectedChequeItems.map((item) => (
                <Box
                  key={item.Id}
                  sx={{
                    p: 2,
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: '#f9fafb'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.BillNumber} - {item.MasterName}
                    </Typography>
                    <Chip
                      label={formatCurrency(item.Amount)}
                      size="small"
                      sx={{ backgroundColor: '#dbeafe', color: '#1e40af' }}
                    />
                  </Box>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={individualChequeStatuses[item.Id] || 1}
                      onChange={(e) => setIndividualChequeStatuses(prev => ({
                        ...prev,
                        [item.Id]: parseInt(e.target.value)
                      }))}
                      label="Status"
                    >
                      <MenuItem value={1}>Pending</MenuItem>
                      <MenuItem value={2}>Passed</MenuItem>
                      <MenuItem value={3}>Bounced</MenuItem>
                      <MenuItem value={4}>Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => {
                setBulkChequeStatusModalOpen(false);
                setIndividualChequeStatuses({});
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkUpdateChequeStatus}
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)'
                }
              }}
            >
              Update All
            </Button>
          </DialogActions>
        </Dialog>

        {/* Payment/Receipt Modal */}
        <PaymentReceiptsModal
          open={openModal}
          onClose={handleCloseModal}
          onSave={handleSave}
          modalAction={modalAction}
          initialData={initialData}
          dropdownData={dropdownData}
          banks={banks}
          paymentMethods={paymentMethods}
          saving={modalLoading}
          Companyid={companyId}
          token={token}
          fetchMaxBillNo={fetchMaxBillNo}
          onPrint={handlePrintPaymentReceipt}
        />

        {/* Success/Error Snackbar */}
        <Snackbar
          open={!!success}
          autoHideDuration={3000}
          onClose={() => setSuccess('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!error}
          autoHideDuration={3000}
          onClose={() => setError('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      </div>
    </Fade>
  );
};

export default PaymentReceiptsIndex;
